import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AuthUser, MemberRole } from '@freelancer-os/shared-types';
import type { InviteMemberInput, UpdateMemberRoleInput } from '@freelancer-os/validation';
import { fetchApi } from '../lib/api';
import i18n from '../i18n';

interface MemberItem {
  id: string;
  role: MemberRole;
  joinedAt: string | null;
  user: AuthUser;
}

interface InvitationItem {
  id: string;
  email: string;
  role: MemberRole;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

interface InvitationResponse {
  invitation: {
    id: string;
    email: string;
    role: MemberRole;
    token: string;
    expiresAt: string;
  };
}

export function useMembers() {
  return useQuery<MemberItem[]>({
    queryKey: ['org', 'members'],
    queryFn: () => fetchApi<MemberItem[]>('/org/members'),
  });
}

export function useInvitations() {
  return useQuery<InvitationItem[]>({
    queryKey: ['org', 'invitations'],
    queryFn: () => fetchApi<InvitationItem[]>('/org/invitations'),
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: InviteMemberInput) =>
      fetchApi<InvitationResponse>('/org/invite', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'invitations'] });
      toast.success(i18n.t('team.toastInvited', { ns: 'settings' }));
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'ALREADY_MEMBER') {
        toast.error(i18n.t('team.toastAlreadyMember', { ns: 'settings' }));
      } else {
        toast.error(err.message || i18n.t('team.toastInviteError', { ns: 'settings' }));
      }
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateMemberRoleInput & { id: string }) =>
      fetchApi<{ message: string }>(`/org/members/${id}/role`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'members'] });
      toast.success(i18n.t('team.toastRoleUpdated', { ns: 'settings' }));
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('team.toastRoleError', { ns: 'settings' }));
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ message: string }>(`/org/members/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'members'] });
      toast.success(i18n.t('team.toastRemoved', { ns: 'settings' }));
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('team.toastRemoveError', { ns: 'settings' }));
    },
  });
}
